import { Easing } from "framer-motion";

// ============================================
// VARIANT STYLES
// ============================================

// Subtle fade - Professional and clean
export const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

// Slide from right - Modern dashboard feel
export const slideRightVariants = {
  initial: { opacity: 0, x: 100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -100 }
};

// Slide from left
export const slideLeftVariants = {
  initial: { opacity: 0, x: -100 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 }
};

// Slide up - Elegant bottom sheet style
export const slideUpVariants = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -50 }
};

// Slide down
export const slideDownVariants = {
  initial: { opacity: 0, y: -50 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 }
};

// Subtle scale - Apple-like smoothness
export const scaleVariants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 }
};

// Scale up - Dashboard card effect
export const scaleUpVariants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.05 }
};

// Rotate with fade - Creative but professional
export const rotateVariants = {
  initial: { opacity: 0, rotate: -5, scale: 0.95 },
  animate: { opacity: 1, rotate: 0, scale: 1 },
  exit: { opacity: 0, rotate: 5, scale: 0.95 }
};

// Blur transition - Premium feel
export const blurVariants = {
  initial: { opacity: 0, filter: "blur(10px)" },
  animate: { opacity: 1, filter: "blur(0px)" },
  exit: { opacity: 0, filter: "blur(10px)" }
};

// Slide and scale - Best of both worlds
export const slideScaleVariants = {
  initial: { opacity: 0, x: 50, scale: 0.95 },
  animate: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: -50, scale: 0.95 }
};

// Fade with slight lift - Floating effect
export const floatVariants = {
  initial: { opacity: 0, y: 20, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.98 }
};

// Zoom in - Attention-grabbing for important pages
export const zoomVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 1.1 }
};

// ============================================
// TRANSITION STYLES
// ============================================

// Ultra smooth - Best for admin panels
export const smoothTransition = {
  type: "tween" as const,
  ease: "easeInOut" as Easing,
  duration: 0.3
};

// Quick and snappy - Fast navigation feel
export const fastTransition = {
  type: "tween" as const,
  ease: "easeOut" as Easing,
  duration: 0.2
};

// Slower and elegant - For important pages
export const slowTransition = {
  type: "tween" as const,
  ease: "anticipate" as Easing,
  duration: 0.5
};

// Spring bounce - Playful but professional
export const springTransition = {
  type: "spring" as const,
  stiffness: 260,
  damping: 20
};

// Soft spring - Gentle bounce
export const softSpringTransition = {
  type: "spring" as const,
  stiffness: 200,
  damping: 25
};

// Tight spring - Responsive feel
export const tightSpringTransition = {
  type: "spring" as const,
  stiffness: 300,
  damping: 30
};

// Fluid transition - Natural movement
export const fluidTransition = {
  type: "tween" as const,
  ease: [0.43, 0.13, 0.23, 0.96] as Easing, // Custom bezier curve
  duration: 0.4
};

// Smooth acceleration - iOS-like
export const smoothAccelerationTransition = {
  type: "tween" as const,
  ease: [0.25, 0.1, 0.25, 1] as Easing, // Ease-in-out curve
  duration: 0.35
};

// ============================================
// RECOMMENDED COMBINATIONS FOR ADMIN PANEL
// ============================================

// 🏆 BEST FOR ADMIN PANELS - Professional and smooth
export const adminPanelDefault = {
  variants: slideScaleVariants,
  transition: smoothTransition
};

// Fast and efficient - For data-heavy pages
export const adminPanelFast = {
  variants: fadeVariants,
  transition: fastTransition
};

// Premium feel - For dashboard/home
export const adminPanelPremium = {
  variants: floatVariants,
  transition: fluidTransition
};

// Modern SaaS style - Clean and professional
export const adminPanelModern = {
  variants: slideRightVariants,
  transition: smoothAccelerationTransition
};

// Elegant - For reports and detailed pages
export const adminPanelElegant = {
  variants: scaleVariants,
  transition: softSpringTransition
};